import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { clamp } from 'lodash';
import { combineLatest, filter } from 'rxjs';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { Axis } from '../../renderers/side-view/side-view.component';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { RendererLayer, setRendererLayers } from '../render-utils';
import { EditPanelCamera } from './edit-panel-camera';
import { SideViewCamera } from './side-view-camera';

@Injectable()
export class SideViewCamerasService extends InjectableSuperclass {
  private readonly store = inject(PointCloudContentStore);
  private readonly shellConnector = inject(shellConnectorToken);

  // TODO: To be removed on POL-1318 (part of Epic POL-1227)
  private oldRightCamera = new SideViewCamera(0);
  private oldBackCamera = new SideViewCamera(1);
  private oldTopCamera = new SideViewCamera(2);

  private rightCamera = new EditPanelCamera(0);
  private backCamera = new EditPanelCamera(1);
  private topCamera = new EditPanelCamera(2);

  private shouldUseNewEditPanels =
    this.shellConnector.hasFeatureFlag('new-edit-panel');

  constructor() {
    super();
    setRendererLayers([RendererLayer.SideViewBottom], this.oldRightCamera);
    setRendererLayers([RendererLayer.SideViewMiddle], this.oldBackCamera);
    setRendererLayers([RendererLayer.SideViewTop], this.oldTopCamera);

    if (this.shouldUseNewEditPanels) {
      setRendererLayers([RendererLayer.SideViewBottom], this.rightCamera);
      setRendererLayers([RendererLayer.SideViewMiddle], this.backCamera);
      setRendererLayers([RendererLayer.SideViewTop], this.topCamera);
      this.setUpPositioningUpdate(0);
      this.setUpPositioningUpdate(1);
      this.setUpPositioningUpdate(2);
    }
  }

  getCameraByRendererLayer(rendererLayer: RendererLayer) {
    if (!this.shouldUseNewEditPanels) {
      return this.oldGetCameraByRendererLayer(rendererLayer);
    }

    switch (rendererLayer) {
      case RendererLayer.SideViewBottom:
        return this.rightCamera;
      case RendererLayer.SideViewMiddle:
        return this.backCamera;
      case RendererLayer.SideViewTop:
        return this.topCamera;
      default:
        throw new Error(`Invalid renderer layer: ${rendererLayer}`);
    }
  }

  getCameraByAxis(axis: Axis) {
    if (!this.shouldUseNewEditPanels) {
      return this.oldGetCameraByAxis(axis);
    }

    switch (axis) {
      case 0:
        return this.rightCamera;
      case 1:
        return this.backCamera;
      case 2:
        return this.topCamera;
    }
  }

  dollyCamera(axis: Axis, dollyFactor: number) {
    const camera = this.getCameraByAxis(axis);
    assertDefined(camera);
    // set to 0.05 to prevent zooming out too much
    camera.zoom = clamp(camera.zoom + dollyFactor, 0.05, 1);
    camera.updateProjectionMatrix();
  }

  resetZoomForAllCameras() {
    if (!this.shouldUseNewEditPanels) {
      return;
    }

    this.rightCamera.zoom = 1;
    this.backCamera.zoom = 1;
    this.topCamera.zoom = 1;

    this.rightCamera.updateProjectionMatrix();
    this.backCamera.updateProjectionMatrix();
    this.topCamera.updateProjectionMatrix();
  }

  private oldGetCameraByAxis(axis: Axis) {
    switch (axis) {
      case 0:
        return this.oldRightCamera;
      case 1:
        return this.oldBackCamera;
      case 2:
        return this.oldTopCamera;
    }
  }

  private oldGetCameraByRendererLayer(rendererLayer: RendererLayer) {
    switch (rendererLayer) {
      case RendererLayer.SideViewBottom:
        return this.oldRightCamera;
      case RendererLayer.SideViewMiddle:
        return this.oldBackCamera;
      case RendererLayer.SideViewTop:
        return this.oldTopCamera;
      default:
        throw new Error(`Invalid renderer layer: ${rendererLayer}`);
    }
  }

  private setUpPositioningUpdate(axis: Axis) {
    const camera = this.getCameraByAxis(axis);

    this.subscribeTo(
      combineLatest([
        this.store.visibleFocusedCuboidLocation$.pipe(filter(isDefined)),
        this.store('render')('currentEditSource').$,
        this.store.getViewRegionStore(axis).$.pipe(filter(isDefined)),
      ]),
      ([focusedCuboid, editSource, viewRegion]) => {
        assert(
          camera instanceof EditPanelCamera,
          'this method should be called with the new-edit-panel feature flag',
        );
        camera.update(focusedCuboid, editSource, viewRegion.size);
      },
    );
  }
}
